import { NextPage } from "next";
import { stringify as qsStringify } from "qs";

import { nextRouterPush, nextRouterReplace } from "@novel/shared/nextRouteOps";

interface CustomerRoutesMap {
    "/": {
        serverProps: {};
        routeParams: {};
        queryParams: {
            address?: string;
            collection?: string;
        };
    };
    "/manage": {
        serverProps: {};
        routeParams: {};
        queryParams: {};
    };
    "/login": {
        serverProps: {};
        routeParams: {};
        queryParams: {
            email?: string;
            code?: string;
            redirectAppUrl?: string;
        };
    };
    "/sign-up": {
        serverProps: {};
        routeParams: {};
        queryParams: {
            email?: string;
            code?: string;
            redirectAppUrl?: string;
        };
    };

    // not used at present
    "/faq": {
        serverProps: {};
        routeParams: {};
        queryParams: {};
    };

    // public link
    "/:customerId": {
        serverProps: {};
        routeParams: { customerId: string };
        queryParams: {};
    };

    // managing auth edge cases, unstyled at present
    "/add-email-for-account": {
        serverProps: {};
        routeParams: {};
        queryParams: {
            sessionToken: string;
        };
    };
    "/claim-email-sent": {
        serverProps: {};
        routeParams: {};
        queryParams: {};
    };
    "/link-expired": {
        serverProps: {};
        routeParams: {};
        queryParams: {};
    };
    "/email-added-for-account": {
        serverProps: {};
        routeParams: {};
        queryParams: {};
    };

    // doesn't really make sense that these are their own pages
    "/authorize/discord": {
        serverProps: {};
        routeParams: {};
        queryParams: {};
    };
    "/authorize/twitter": {
        serverProps: {};
        routeParams: {};
        queryParams: {};
    };
}

export type CustomerRoute = keyof CustomerRoutesMap;

export type CustomerTypedRoute<SpecificRoute extends CustomerRoute> = NextPage<{
    // wrapping in "props" to avoid conflict with native React props (such as "ref")
    props: CustomerRoutesMap[SpecificRoute]["routeParams"] &
        CustomerRoutesMap[SpecificRoute]["queryParams"] &
        CustomerRoutesMap[SpecificRoute]["serverProps"];
}> & {
    isPublicRoute?: boolean;
    isLoggedOutRoute?: boolean;
    isSuperUserRoute?: boolean;
};

export function customerRoutePush<R extends CustomerRoute>(
    customerRoute: R,
    routeParams: CustomerRoutesMap[R]["routeParams"] = {},
    queryParams: CustomerRoutesMap[R]["queryParams"] = {},
): Promise<boolean> {
    return nextRouterPush(mapParamsObjectToRoute(customerRoute, routeParams, queryParams));
}

export function customerRouteReplace<R extends CustomerRoute>(
    customerRoute: R,
    routeParams: CustomerRoutesMap[R]["routeParams"] = {},
): Promise<boolean> {
    return nextRouterReplace(mapParamsObjectToRoute(customerRoute, routeParams));
}

function mapParamsObjectToRoute<R extends CustomerRoute>(
    customerRoute: R,
    routeParams: CustomerRoutesMap[R]["routeParams"] = {},
    queryParams: CustomerRoutesMap[R]["queryParams"] = {},
) {
    const route = Object.keys(routeParams).reduce((accum: string, paramName: string) => {
        const castedParamName = paramName as keyof CustomerRoutesMap[R]["routeParams"];
        const paramValue = routeParams[castedParamName] as unknown as string; // unsure why this is necessary
        return accum.replace(`:${paramName}`, encodeURIComponent(paramValue));
    }, customerRoute);

    return `${route}${qsStringify(queryParams, { addQueryPrefix: true })}`;
}
